import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { makeInitialLayouts, BREAKPOINTS, removeItem, Breakpoint, ResponsiveLayouts } from '../grid.utils'
import { CustomReportSectionsType } from '../../../../../redux/context/customReports/typesSection'
import { useCtxUpdateCustomReport } from '../../../../../features/customReport/queries/report/useCtxUpdateCustomReport'
import { useCalculatedReport } from '../CalculatedReportContext'

export type LayoutsContextType = {
  layouts?: ResponsiveLayouts
  currentBreakPoint: Breakpoint
  editedLayouts?: ResponsiveLayouts
  layoutEdit: boolean
  onSectionRemove: (section: CustomReportSectionsType) => void
  setLayoutEdit: (edit: boolean) => void
  setCurrentBreakPoint: (breakpoint: Breakpoint) => void
  handleLayoutSubmit: (newLayouts?: ResponsiveLayouts) => void
  setEditedLayouts: (layouts?: ResponsiveLayouts) => void
}

export const LayoutsContext = React.createContext<LayoutsContextType | null>(null)

interface LayoutsProviderProps {
  children?: ReactNode
}

const LayoutsProvider: React.FC<LayoutsProviderProps> = ({ children }) => {
  const { data: calculatedReport } = useCalculatedReport()
  const [layoutEdit, setLayoutEdit] = useState<boolean>(false)
  const [layouts, setLayouts] = useState(calculatedReport?.layouts)
  const [editedLayouts, setEditedLayouts] = useState<ResponsiveLayouts>()
  const [currentBreakPoint, setCurrentBreakPoint] = useState<Breakpoint>('lg')

  const { mutateAsync: updateReport } = useCtxUpdateCustomReport()

  useEffect(() => {
    const initial = makeInitialLayouts(Object.keys(BREAKPOINTS) as Breakpoint[], calculatedReport?.sections)
    let newLayouts: ResponsiveLayouts | undefined

    if (calculatedReport?.sections?.length) {
      // Get current section IDs to filter out deleted sections from existing layouts
      const currentSectionIds = new Set(calculatedReport.sections.map(s => s.id.toString()))

      // Filter existing layouts to only include sections that still exist
      const filteredExistingLayouts = calculatedReport?.layouts
        ? {
            ...calculatedReport.layouts,
            lg: calculatedReport.layouts.lg?.filter(item => currentSectionIds.has(item.i)) || [],
            xxs: calculatedReport.layouts.xxs?.filter(item => currentSectionIds.has(item.i)) || []
          }
        : undefined

      // Merge layouts by id, not by array index
      if (filteredExistingLayouts) {
        newLayouts = (Object.keys(BREAKPOINTS) as Breakpoint[]).reduce((acc, bp) => {
          const existingMap = new Map(filteredExistingLayouts[bp]?.map(item => [item.i, item]))
          const initialItems = initial[bp] || []

          // Calculate max y + h from existing layouts for new sections
          const maxBottom = filteredExistingLayouts[bp]?.reduce((max, item) => Math.max(max, item.y + item.h), 0) || 0

          acc[bp] = initialItems.map(item => {
            const existing = existingMap.get(item.i)
            if (existing) {
              return existing
            }
            // New section - place at the bottom
            return { ...item, y: maxBottom }
          })

          return acc
        }, {} as ResponsiveLayouts)
      } else {
        newLayouts = initial
      }
    }

    setLayouts(newLayouts)

    // If layout data has changed (sections added or removed), update the backend
    if (newLayouts && calculatedReport?.layouts) {
      const existingLayoutIds = new Set(calculatedReport.layouts.lg?.map(item => item.i) || [])
      const newLayoutIds = new Set(newLayouts.lg?.map(item => item.i) || [])
      const hasNewSections = [...newLayoutIds].some(id => !existingLayoutIds.has(id))
      const hasRemovedSections = [...existingLayoutIds].some(id => !newLayoutIds.has(id))

      if (hasNewSections || hasRemovedSections) {
        // Auto-save the updated layout for added/removed sections (without showing success message)
        updateReport({
          data: { id: calculatedReport?.id, layouts: newLayouts },
          options: { showSuccess: false, skipInvalidation: true }
        })
      }
    }

    return () => {
      setLayouts(undefined)
    }
  }, [calculatedReport?.id, calculatedReport?.sections?.length, updateReport])

  const handleLayoutSubmit = useCallback(async () => {
    if (!calculatedReport?.id) return
    const layoutsToSave = editedLayouts ?? layouts
    await updateReport({
      data: { id: calculatedReport.id, layouts: layoutsToSave },
      options: { skipInvalidation: true }
    })
    setEditedLayouts(undefined)
    setLayouts(layoutsToSave)
    setLayoutEdit(false)
  }, [calculatedReport?.id, editedLayouts, layouts, updateReport])

  const onSectionRemove = useCallback(
    (section: CustomReportSectionsType) => setLayouts(current => removeItem(section.id.toString(), current)),
    []
  )

  const value = useMemo(
    () => ({
      layouts,
      layoutEdit,
      editedLayouts,
      currentBreakPoint,
      setCurrentBreakPoint,
      setLayoutEdit,
      onSectionRemove,
      setEditedLayouts,
      handleLayoutSubmit
    }),
    [layouts, layoutEdit, editedLayouts, currentBreakPoint, onSectionRemove, handleLayoutSubmit]
  )

  return <LayoutsContext.Provider value={value}>{children}</LayoutsContext.Provider>
}

export default LayoutsProvider
